import { firebase } from "astrid-firebase";
import db from "astrid-firebase/src/db";
import organizationTypes from "astrid-firestore/src/api/organizations/constants/organizationTypes";
import producerRoles from "astrid-firestore/src/api/organizations/constants/producerRoles";
import {
	isProductionOccupied,
	isReaderOccupied,
	isRecorderOccupied,
	isStudioOccupied,
} from "astrid-firestore/src/api/sessions/utils/sessionOverlap";
import { useLoadingValues } from "astrid-firestore/src/hooks";
import useCollectionDataByIdsOnce from "astrid-firestore/src/hooks/useCollectionDataByIdsOnce";
import useCollectionDataOnce from "astrid-firestore/src/hooks/useCollectionDataOnce";
import uniqueArray from "astrid-helpers/src/uniqueArray";

import CalendarEngineerResourceTitle from "../components/CalendarEngineerResourceTitle";
import CalendarReaderResourceTitle from "../components/CalendarReaderResourceTitle";
import CalendarStudioResourceTitle from "../components/CalendarStudioResourceTitle";

import useCalendarEngineerResources from "./useCalendarEngineerResources";
import useCalendarReaderResources from "./useCalendarReaderResources";
import useCalendarStudioResources from "./useCalendarStudioResources";

export default function useCalendarResourceTypes({ organization, sessions }) {
	const readerIds = uniqueArray(sessions.flatMap(({ reader }) => reader));

	const [[studios = [], engineers = [], readers = []], loading, error] = useLoadingValues(
		useCollectionDataOnce(
			organization &&
				organization.type !== organizationTypes.NARRATOR &&
				db.collection("studios").where("producerId", "==", organization.id).where("active", "==", true),
		),
		useCollectionDataOnce(
			organization &&
				organization.type !== organizationTypes.NARRATOR &&
				db
					.collection("users")
					.where(`permissions.producer.${organization.id}`, "array-contains", producerRoles.RECORDER),
		),
		useCollectionDataByIdsOnce(firebase, db.collection("users"), organization && readerIds),
	);

	const studioResources = useCalendarStudioResources({ studios });
	const engineerResources = useCalendarEngineerResources({ engineers });
	const readerResources = useCalendarReaderResources({ readers });

	const resourceTypes = {
		studios: {
			resources: studioResources.resources,
			resourceColors: studioResources.resourceColors,
			resourceTitle: (props) => <CalendarStudioResourceTitle {...props} />,
			getSessionOrder: (session) => {
				const studio = studios.find((studio) => studio.id === session.studio);

				return studio ? `${studio.groupTags[0]} ${studio.name}` : "";
			},
			getResourceId: (session) => session.studio || session.studioGroup,
			getOverlappingResourceIds: () => studios.map((studio) => studio.id),
			checkOverlap: (session, { recorder, reader, production, productionId }) => {
				return (
					isRecorderOccupied({ session, recorder }) ||
					isReaderOccupied({ session, reader }) ||
					isProductionOccupied({ session, production: production || productionId })
				);
			},
		},
		engineers: {
			resources: engineerResources.resources,
			resourceColors: engineerResources.resourceColors,
			resourceTitle: (props) => <CalendarEngineerResourceTitle {...props} />,
			getResourceId: (session) => session.recorder,
			getOverlappingResourceIds: (session) => [session.recorder],
			checkOverlap: (session, { reader, studio, production, productionId }) => {
				return (
					isStudioOccupied({ session, studio }) ||
					isReaderOccupied({ session, reader }) ||
					isProductionOccupied({ session, production: production || productionId })
				);
			},
		},
		readers: {
			resources: readerResources.resources,
			resourceColors: readerResources.resourceColors,
			resourceTitle: (props) => <CalendarReaderResourceTitle {...props} />,
			getResourceId: (session) => session.reader.find((readerId) => readerId),
			getOverlappingResourceIds: (session) => session.reader.find((readerId) => readerId),
			checkOverlap: (session, { reader, studio, production, productionId }) => {
				return (
					isStudioOccupied({ session, studio }) ||
					isReaderOccupied({ session, reader }) ||
					isProductionOccupied({ session, production: production || productionId })
				);
			},
		},
		productions: {
			resources: [],
			resourceColors: {},
			getResourceId: (session) => session.production,
		},
	};

	return {
		resourceTypes,
		loading,
		error,
		getBackgroundColor: (session) => studios.find((studio) => studio.id === session.studio)?.color,
	};
}
